import axios, { AxiosInstance } from 'axios';

import { Configuration } from 'src/Services/API/generated';
import {
  AnnouncementDto,
  AssetAnnouncementDto,
  CreateAnnouncementDto,
  CreateAssetAnnouncementDto,
  PublicAnnouncementDto,
  UpdateAnnouncementDto,
  UpdateAssetAnnouncementDto,
} from 'src/types/announcement.types';

export class AnnouncementApi {
  private axiosInstance: AxiosInstance;

  constructor(configuration: Configuration) {
    this.axiosInstance = axios.create({
      baseURL: configuration.basePath || '',
      ...configuration.baseOptions,
    });
  }

  async findAll(): Promise<AnnouncementDto[]> {
    const { data } = await this.axiosInstance.get<AnnouncementDto[]>('/internal/announcements');
    return data;
  }

  async create(dto: CreateAnnouncementDto): Promise<AnnouncementDto> {
    const { data } = await this.axiosInstance.post<AnnouncementDto>('/internal/announcements', dto);
    return data;
  }

  async update(id: string, dto: UpdateAnnouncementDto): Promise<AnnouncementDto> {
    const { data } = await this.axiosInstance.patch<AnnouncementDto>(
      `/internal/announcements/${id}`,
      dto
    );
    return data;
  }

  async publish(id: string): Promise<AnnouncementDto> {
    const { data } = await this.axiosInstance.post<AnnouncementDto>(
      `/internal/announcements/${id}/publish`
    );
    return data;
  }

  async unpublish(id: string): Promise<AnnouncementDto> {
    const { data } = await this.axiosInstance.post<AnnouncementDto>(
      `/internal/announcements/${id}/unpublish`
    );
    return data;
  }

  async delete(id: string): Promise<void> {
    await this.axiosInstance.delete(`/internal/announcements/${id}`);
  }

  async findActive(): Promise<PublicAnnouncementDto | null> {
    const { data } = await this.axiosInstance.get<PublicAnnouncementDto | null>(
      '/public/announcements/active'
    );
    return data;
  }

  // --- Asset-scoped ---

  async findAllByAsset(assetId: string): Promise<AssetAnnouncementDto[]> {
    const { data } = await this.axiosInstance.get<AssetAnnouncementDto[]>(
      `/assets/${assetId}/announcements`
    );
    return data;
  }

  async findActiveByAsset(assetId: string): Promise<AssetAnnouncementDto | null> {
    const { data } = await this.axiosInstance.get<AssetAnnouncementDto | null>(
      `/assets/${assetId}/announcements/active`
    );
    return data;
  }

  async createForAsset(
    assetId: string,
    dto: CreateAssetAnnouncementDto
  ): Promise<AssetAnnouncementDto> {
    const { data } = await this.axiosInstance.post<AssetAnnouncementDto>(
      `/assets/${assetId}/announcements`,
      dto
    );
    return data;
  }

  async updateForAsset(
    assetId: string,
    id: string,
    dto: UpdateAssetAnnouncementDto
  ): Promise<AssetAnnouncementDto> {
    const { data } = await this.axiosInstance.patch<AssetAnnouncementDto>(
      `/assets/${assetId}/announcements/${id}`,
      dto
    );
    return data;
  }

  async publishForAsset(assetId: string, id: string): Promise<AssetAnnouncementDto> {
    const { data } = await this.axiosInstance.post<AssetAnnouncementDto>(
      `/assets/${assetId}/announcements/${id}/publish`
    );
    return data;
  }

  async unpublishForAsset(assetId: string, id: string): Promise<AssetAnnouncementDto> {
    const { data } = await this.axiosInstance.post<AssetAnnouncementDto>(
      `/assets/${assetId}/announcements/${id}/unpublish`
    );
    return data;
  }

  async deleteForAsset(assetId: string, id: string): Promise<void> {
    await this.axiosInstance.delete(`/assets/${assetId}/announcements/${id}`);
  }

  async dismissForAsset(assetId: string, id: string): Promise<void> {
    await this.axiosInstance.post(`/assets/${assetId}/announcements/${id}/dismiss`);
  }
}
